import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
    AlertTriangle, Loader2, ChevronRight, X, ArrowRight, ArrowLeft, Check, ShieldAlert, CheckCircle2, CircleDashed
} from 'lucide-react';
import { ACTIVE_MAPS } from '../config/mapPool';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface TeamMapPriorityWizardProps {
    teamId: string;
    onComplete: () => void;
    onBack: () => void;
}

export function TeamMapPriorityWizard({ teamId, onComplete, onBack }: TeamMapPriorityWizardProps) {
    const { t } = useTranslation();
    const [exiting, setExiting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [columnError, setColumnError] = useState<'Ban' | 'Pick' | null>(null);

    // State tracks which column a map belongs to
    // By default, all ACTIVE_MAPS are in 'Open'
    const [mapState, setMapState] = useState<Record<string, 'Ban' | 'Open' | 'Pick'>>(() => {
        const initialState: Record<string, 'Ban' | 'Open' | 'Pick'> = {};
        ACTIVE_MAPS.forEach(map => {
            initialState[map.id] = 'Open';
        });
        return initialState;
    });

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, mapId: string) => {
        e.dataTransfer.setData('mapId', mapId);
        // Add a slight visual effect to drag image (optional)
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault(); // allow dropping
        e.dataTransfer.dropEffect = 'move';
    };

    const requestMoveMap = (mapId: string, column: 'Ban' | 'Open' | 'Pick') => {
        if (mapState[mapId] === column) return;

        if (column === 'Pick' || column === 'Ban') {
            const count = Object.values(mapState).filter(c => c === column).length;
            if (count >= 3) {
                setColumnError(column);
                setTimeout(() => setColumnError(null), 1500);
                return;
            }
        }

        setMapState(prev => ({
            ...prev,
            [mapId]: column
        }));
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, column: 'Ban' | 'Open' | 'Pick') => {
        e.preventDefault();
        const mapId = e.dataTransfer.getData('mapId');
        if (mapId) {
            requestMoveMap(mapId, column);
        }
    };

    const moveMap = (mapId: string, to: 'Ban' | 'Open' | 'Pick') => {
        requestMoveMap(mapId, to);
    };

    const handleSave = async () => {
        setLoading(true);
        setError('');
        try {
            // Transform local mapState to MapPreferences array payload expected by the backend
            const mapPreferences = Object.entries(mapState).map(([mapId, status]) => {
                let formattedStatus = 'neutral';
                if (status === 'Ban') formattedStatus = 'main_ban';
                if (status === 'Pick') formattedStatus = 'main_pick';
                
                return {
                    mapId,
                    status: formattedStatus,
                    progress: 0 // Optional progress, keep as 0 initially
                };
            });

            const token = localStorage.getItem('token');
            await axios.patch(`${API}/teams/${teamId}/map-preferences`, 
                { mapPreferences }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setExiting(true);
            setTimeout(() => onComplete(), 300);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save map priorities.');
            setLoading(false);
        }
    };

    const COLUMNS: { id: 'Ban' | 'Open' | 'Pick', label: string, color: string, border: string, bg: string, icon: React.ReactNode }[] = [
        { 
            id: 'Ban', 
            label: 'Ban', 
            color: 'text-red-400', 
            border: 'border-red-500/30', 
            bg: 'bg-red-500/5', 
            icon: <ShieldAlert size={16} /> 
        },
        { 
            id: 'Open', 
            label: 'Open', 
            color: 'text-amber-400', 
            border: 'border-amber-500/30', 
            bg: 'bg-amber-500/5', 
            icon: <CircleDashed size={16} /> 
        },
        { 
            id: 'Pick', 
            label: 'Pick', 
            color: 'text-emerald-400', 
            border: 'border-emerald-500/30', 
            bg: 'bg-emerald-500/5', 
            icon: <CheckCircle2 size={16} /> 
        }
    ];

    return (
        <div className={`w-[900px] h-[600px] bg-slate-900 border border-slate-700/50 rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden relative transform scale-100 transition-all duration-300 ${exiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
            <style>
                {`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-6px); }
                    50% { transform: translateX(6px); }
                    75% { transform: translateX(-6px); }
                }
                `}
            </style>
            
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-6 border-b border-slate-700/30 text-center relative bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-amber-500/10 to-transparent pointer-events-none" />
                <h2 className="text-xl font-bold text-white tracking-tight relative z-10">Map Priorities</h2>
                <p className="text-slate-400 text-xs max-w-lg mx-auto mt-2 relative z-10">
                    Drag and drop your active maps to set your team's tactical priorities. We'll set up your stratbook accordingly.
                </p>
            </div>

            {/* Content Body: 3 Columns drag-off area */}
            <div className="flex-1 min-h-0 bg-slate-950 p-6 flex gap-4">
                {COLUMNS.map(col => (
                    <div 
                        key={col.id}
                        className={`flex-1 flex flex-col rounded-xl border border-dashed transition-all duration-300 relative overflow-hidden ${col.border} ${col.bg} ${columnError === col.id ? 'animate-[shake_0.4s_ease-in-out] border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)]' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, col.id)}
                    >
                        {/* Error Overlay */}
                        {columnError === col.id && (
                            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-950/80 backdrop-blur-md animate-in fade-in zoom-in-95 duration-200">
                                <AlertTriangle size={36} className="text-red-500 mb-3 animate-pulse" strokeWidth={2.5} />
                                <span className="text-white font-black text-sm uppercase tracking-wider text-center px-4">
                                    Max 3 Maps
                                </span>
                            </div>
                        )}

                        <div className={`shrink-0 flex flex-col items-center justify-center p-3 border-b ${col.border} bg-slate-900/50`}>
                            <div className={`flex items-center gap-2 font-bold uppercase tracking-widest text-[11px] ${col.color}`}>
                                {col.icon}
                                {col.label}
                            </div>
                        </div>

                        <div className="flex-1 p-4 grid grid-cols-2 gap-3 content-start min-h-[100px]">
                            {ACTIVE_MAPS.filter(map => mapState[map.id] === col.id).map(map => (
                                <div 
                                    key={map.id} 
                                    draggable 
                                    onDragStart={(e) => handleDragStart(e, map.id)}
                                    className="group relative w-full aspect-video rounded-xl bg-slate-800 border-2 border-slate-700/80 hover:border-slate-400 shadow-[0_4px_20px_rgba(0,0,0,0.5)] cursor-grab active:cursor-grabbing transition-all hover:scale-105 hover:z-10 animate-in fade-in zoom-in-95 duration-200 overflow-hidden"
                                    title={map.name}
                                >
                                    <img 
                                        src={map.image} 
                                        alt={map.name} 
                                        className="w-full h-full object-cover pointer-events-none" 
                                        draggable={false} 
                                    />
                                    {/* Subtle gradient overlay to make it look premium */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                                    <div className="absolute bottom-1.5 left-2 font-black text-[10px] text-white/90 drop-shadow-md uppercase tracking-wider pointer-events-none">
                                        {map.name}
                                    </div>
                                </div>
                            ))}
                            {ACTIVE_MAPS.filter(map => mapState[map.id] === col.id).length === 0 && (
                                <div className="col-span-2 h-32 flex items-center justify-center pointer-events-none opacity-40">
                                    <div className={`text-xs font-bold uppercase tracking-wider border-2 border-dashed rounded-xl px-4 py-3 ${col.color} border-current`}>
                                        Drop Maps Here
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 p-5 border-t border-slate-700/30 flex items-center justify-between bg-slate-900">
                <div className="flex-1 flex items-center gap-4">
                    <button 
                        className="team-wizard-back px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                        onClick={() => {
                            setExiting(true);
                            setTimeout(() => onBack(), 300);
                        }}
                        disabled={loading}
                    >
                        Go Back
                    </button>
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2 w-max animate-in fade-in">
                            <AlertTriangle size={12} />
                            {error}
                        </div>
                    )}
                </div>
                <button 
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-2.5 rounded-lg font-bold transition-all shadow-[0_0_15px_rgba(37,99,235,0.4)] tracking-wide disabled:opacity-50"
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : 'Continue'}
                    {!loading && <ChevronRight size={16} />}
                </button>
            </div>
        </div>
    );
}
